// シフト決定画面の全画面表示トグル
// - ブラウザの全画面（Fullscreen API）ではなく、#lecturer-schedule-decision を position: fixed / inset: 0 で
//   ページいっぱいに広げる（サイドバーやヘッダーの上に重ねる）
// - 広がる／戻るアニメーションは View Transitions API（document.startViewTransition）に任せる。
//   見た目の指定は resources/css/app.css の ::view-transition-*(lecturer-schedule-decision)
// - View Transitions API が無いブラウザと、動きを減らす設定のときはアニメーション無しで切り替える
// - 全画面の切り替えでは DOM を作り直さないので、入力中の内容や一覧のスクロール位置はそのまま残る
//
// 必要な DOM（index.blade.php）:
//   #lecturer-schedule-decision  画面のルート（.is-full が付いている間が全画面）
//   .btn-toggle-full             切り替えボタン（Material Symbols のリガチャを textContent で持つ）

/** 全画面のときにルートへ付けるクラス（見た目は app.css 側で定義する） */
const FULL_CLASS = 'is-full'
/** 全画面のときに body へ付けるクラス（背後のページをスクロールさせない） */
const BODY_FULL_CLASS = 'has-full-screen-panel'

const ICON_OPEN = 'open_in_full'
const ICON_CLOSE = 'close_fullscreen'
const LABEL_OPEN = '全画面表示にする'
const LABEL_CLOSE = '全画面表示を終了する'

/**
 * 動きを減らす設定かどうか
 *
 * @returns {boolean}
 */
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * ボタンのアイコンと説明を今の状態に合わせる
 *
 * @param {HTMLElement} button
 * @param {boolean} isFull
 * @returns {void}
 */
const syncButton = (button, isFull) => {
    button.textContent = isFull ? ICON_CLOSE : ICON_OPEN
    button.setAttribute('aria-label', isFull ? LABEL_CLOSE : LABEL_OPEN)
    button.setAttribute('title', isFull ? LABEL_CLOSE : LABEL_OPEN)
    button.setAttribute('aria-pressed', isFull ? 'true' : 'false')
}

/**
 * 全画面表示の切り替えを設定する（ボタンは描き直しの対象外なので一度だけ呼ぶ）
 *
 * @param {HTMLElement} root #lecturer-schedule-decision
 * @returns {void}
 */
export const setUpFullScreenToggle = (root) => {
    const button = root.querySelector('.btn-toggle-full')
    if (!button) {
        return
    }

    // 全画面にするとルートが position: fixed になりページの高さが変わるため、戻すときに元の位置へスクロールし直す
    let scrollY = 0

    /**
     * 全画面の状態を切り替える（View Transitions API のコールバックから呼ぶので DOM の更新だけを行う）
     *
     * @param {boolean} toFull
     * @returns {void}
     */
    const apply = (toFull) => {
        root.classList.toggle(FULL_CLASS, toFull)
        document.body.classList.toggle(BODY_FULL_CLASS, toFull)
        syncButton(button, toFull)
        if (!toFull) {
            window.scrollTo(0, scrollY)
        }
    }

    const toggle = () => {
        const toFull = !root.classList.contains(FULL_CLASS)
        if (toFull) {
            scrollY = window.scrollY
        }

        // 未対応のブラウザ・動きを減らす設定では、アニメーション無しで切り替える
        if (prefersReducedMotion() || typeof document.startViewTransition !== 'function') {
            apply(toFull)
            return
        }

        document.startViewTransition(() => apply(toFull))
    }

    button.addEventListener('click', toggle)

    // Esc で全画面を終了する
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && root.classList.contains(FULL_CLASS)) {
            toggle()
        }
    })

    syncButton(button, root.classList.contains(FULL_CLASS))
}
